// @ts-nocheck
import { useQueryClient, useMutation } from 'react-query';
import { useRequestQuery } from '../useQueryRequest';
import { transformPagination } from '@/utils';
import useApiRequest from '../useRequest';
import { useRequestPdf } from '../useRequestPdf';
import t from './types';

// Common invalidate queries for sales orders.
const commonInvalidateQueries = (queryClient) => {
  // Invalidate sales orders.
  queryClient.invalidateQueries(t.SALES_ORDERS);
  queryClient.invalidateQueries(t.SALES_ORDER);

  // Invalidate customers.
  queryClient.invalidateQueries(t.CUSTOMERS);

  // Invalidate items.
  queryClient.invalidateQueries(t.ITEMS);
  queryClient.invalidateQueries(t.ITEM);

  // Invalidate settings.
  queryClient.invalidateQueries([t.SETTING, t.SETTING_SALES_ORDERS]);

  // Invalidate financial reports.
  queryClient.invalidateQueries(t.FINANCIAL_REPORT);

  // Invalidate transactions by reference.
  queryClient.invalidateQueries(t.TRANSACTIONS_BY_REFERENCE);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ACCOUNTS);
  queryClient.invalidateQueries(t.ACCOUNT);

  // Invalidate reconcile.
  queryClient.invalidateQueries(t.RECONCILE_CREDIT_NOTE);
  queryClient.invalidateQueries(t.RECONCILE_CREDIT_NOTES);

  // Invalidate
  queryClient.invalidateQueries(t.ITEM_ASSOCIATED_WITH_SALES_ORDERS);

  // Invalidate item warehouses.
  queryClient.invalidateQueries(t.ITEM_WAREHOUSES_LOCATION);

  // Invalidate mutate base currency abilities.
  queryClient.invalidateQueries(t.ORGANIZATION_MUTATE_BASE_CURRENCY_ABILITIES);
};

/**
 * Creates a new sales order.
 */
export function useCreateSalesOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('sales/salesorders', values), {
    onSuccess: (res, values) => {
      // Invalidate sales order customer.
      queryClient.invalidateQueries([t.CUSTOMER, values.customer_id]);
      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edits the given sales order.
 */
export function useEditSalesOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/salesorders/${id}`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Invalidate specific sales order.
        queryClient.invalidateQueries([t.SALES_ORDER, id]);
        // Invalidate sales order customer.
        queryClient.invalidateQueries([t.CUSTOMER, values.customer_id]);
        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Deletes the given sales order.
 */
export function useDeleteSalesOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`sales/salesorders/${id}`), {
    onSuccess: (res, id) => {
      // Invalidate specific sales order.
      queryClient.invalidateQueries([t.SALES_ORDER, id]);
      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

const transformSalesOrders = (res) => ({
  salesOrders: res.data.sales_orders,
  pagination: transformPagination(res.data.pagination),
  filterMeta: res.data.filter_meta,
});

/**
 * Retrieve sales orders list with pagination meta.
 */
export function useSalesOrders(query, props) {
  // console.log('useSalesOrders - Coming up to here check out this imp query ', query);
  // console.log('useSalesOrders - Coming up to here check out this imp props ', props);
  return useRequestQuery(
    [t.SALES_ORDERS, query],
    { method: 'get', url: 'sales/salesorders', params: query },
    {
      select: transformSalesOrders,
      defaultData: {
        salesOrders: [],
        pagination: {
          page: 1,
          pageSize: 20,
          total: 0,
        },
        filterMeta: {},
      },
      ...props,
    },
  );
}

/**
 * Retrieve the sales order details.
 * @param {number} orderId - Sales order id.
 */
export function useSalesOrder(orderId, props, requestProps) {
  return useRequestQuery(
    [t.SALES_ORDER, orderId],
    { method: 'get', url: `sales/salesorders/${orderId}`, ...requestProps },
    {
      select: (res) => res.data.sales_order,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the sales order pdf document data.
 */
export function usePdfSalesOrder(orderId) {
  return useRequestPdf({
    url: `sales/salesorders/${orderId}`,
  });
}

/**
 * Retrieve due sales orders of the given customer id.
 * @param {number} customerId - Customer id.
 */
export function useDueSalesOrders(customerId, props) {
  return useRequestQuery(
    [t.SALES_ORDERS, t.SALES_ORDERS_DUE, customerId],
    {
      method: 'get',
      url: `sales/salesorders/payable`,
      params: { customer_id: customerId },
    },
    {
      select: (res) => res.data.sales_orders,
      defaultData: [],
      ...props,
    },
  );
}

export function useRefreshSalesOrders() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.SALES_ORDERS);
    },
  };
}

/**
 * Send sales order by mail.
 */
export function useSendSalesOrderMail(props) {
  console.log('useSendSalesOrderMail - Coming up to here chekc out this imp ', props);
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/salesorders/${id}/mail`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Common invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Get default mail options for a sales order.
 */
export function useSalesOrderDefaultOptions(salesOrderId, props) {
  console.log('useSalesOrderDefaultOptions - Coming up to here check out this imp ', salesOrderId);
  return useRequestQuery(
    [t.SALES_ORDER_DEFAULT_OPTIONS, salesOrderId],
    {
      method: 'get',
      url: `sales/salesorders/${salesOrderId}/mail`,
    },
    {
      select: (res) => res.data.data,
      ...props,
    },
  );
}


export function useSalesOrderPaymentTransactions(invoiceId, props) {
  return useRequestQuery(
    [t.SALE_INVOICE_PAYMENT_TRANSACTIONS, invoiceId],
    {
      method: 'get',
      url: `sales/invoices/${invoiceId}/payment-transactions`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}


/**
 * Retrieve the E-SalesOrder details.
 * @param {number} salesOrderId - Sales Order ID.
 */
export function useESalesOrder(salesOrderId, props, requestProps) {
  return useRequestQuery(
    [t.E_SALES_ORDER, salesOrderId],
    { method: 'get', url: `sales/orders/${salesOrderId}/e_sales_order`, ...requestProps },
    {
      select: (res) => res.data,
      defaultData: {},
      ...props,
    },
  );
}

export function useSalesOrderAccounting(salesOrderId, props) {
  return useRequestQuery(
    ['SALES_ORDER_ACCOUNTING', salesOrderId],
    { method: 'get', url: `sales/salesorders/${salesOrderId}/accounting` },
    {
      select: (res) => res.data.data,
      defaultData: { invoices: [], expenses: [], deposits: [] },
      ...props,
    },
  );
}
export function useSalesOrderBillTotal(salesOrderId, props?) {
  return useRequestQuery(
    [t.SALES_ORDER_BILL_TOTAL, salesOrderId],
    { method: 'get', url: `sales/salesorders/${salesOrderId}/bills/billtotal` },
    {
      enabled: !!salesOrderId,
      select: (res) => {
        const responseData = res.data?.data ?? res.data ?? {};
        const billTotal =
          responseData?.billTotalAmount ??
          responseData?.bill_total_amount ??
          res.data?.billTotalAmount ??
          res.data?.bill_total_amount ??
          0;

        return Number(billTotal) || 0;
      },
      defaultData: 0,
      ...props,
    },
  );
}
